<template>
  <div class="flex">
    <!-- Sidebar -->
    <SidebarComponent />

    <!-- Conținut principal -->
    <div class="flex-1 p-8 text-text-light">
      <h1 class="text-3xl font-bold mb-6 text-accent-light">Raport Săptămânal</h1>

      <!-- Bara de căutare -->
      <div class="mb-6">
        <input
          v-model="searchQuery"
          @input="handleSearch"
          type="text"
          placeholder="Caută după nume..."
          class="w-full p-3 border border-accent-light rounded-lg bg-primary-dark text-text-light placeholder-accent-light focus:outline-none focus:ring-2 focus:ring-accent-light focus:border-transparent"
        />
      </div>

      <!-- Buton Descărcare PDF -->
      <div class="mb-6 text-right">
        <button
          @click="generatePDF"
          class="bg-accent-dark text-white p-3 rounded-lg hover:bg-accent-light"
        >
          Descarcă Raportul PDF
        </button>
      </div>

      <div v-if="error" class="text-center text-red-500 font-semibold mb-4">
        Eroare la încărcarea datelor. Verifică conexiunea.
      </div>

      <div v-if="filteredData.length > 0">
        <!-- Tabele pentru roluri principale -->
        <div v-for="(members, role) in groupedMembers" :key="role" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">{{ role }}</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th
                    v-for="stat in roleColumns[role]"
                    :key="stat"
                    class="p-2"
                  >
                    {{ stat }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in members"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': members.indexOf(member) % 2 === 0,
                    'bg-row-dark': members.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td
                    v-for="stat in roleColumns[role]"
                    :key="stat"
                    class="p-2"
                  >
                    {{ member[stat] || 0 }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Tabele pentru roluri adiționale -->
        <div v-if="membersWithResurseRole.length" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">Resurse</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th class="p-2">Teste PD Sustinute</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in membersWithResurseRole"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': membersWithResurseRole.indexOf(member) % 2 === 0,
                    'bg-row-dark': membersWithResurseRole.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td class="p-2">{{ member.teste_pd_sustinute }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="membersWithROLMDTRole.length" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">ROL MDT</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th class="p-2">Mandate Create</th>
                  <th class="p-2">Caziere Create</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in membersWithROLMDTRole"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': membersWithROLMDTRole.indexOf(member) % 2 === 0,
                    'bg-row-dark': membersWithROLMDTRole.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td class="p-2">{{ member.mandate_create }}</td>
                  <td class="p-2">{{ member.caziere_create }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="membersWithTesterMDTRole.length" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">Teste MDT & RADIO</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th class="p-2">Test MDT</th>
                  <th class="p-2">Test RADIO</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in membersWithTesterMDTRole"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': membersWithTesterMDTRole.indexOf(member) % 2 === 0,
                    'bg-row-dark': membersWithTesterMDTRole.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td class="p-2">{{ member.test_MDT }}</td>
                  <td class="p-2">{{ member.test_RADIO }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Instructor Tables -->
        <div v-if="membersWithInstructorPilotRole.length" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">Instructor Pilot</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th class="p-2">Pilot</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in membersWithInstructorPilotRole"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': membersWithInstructorPilotRole.indexOf(member) % 2 === 0,
                    'bg-row-dark': membersWithInstructorPilotRole.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td class="p-2">{{ member.pilot }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="membersWithInstructorMotoHSRole.length" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">Instructor Moto HS</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th class="p-2">Moto HS</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in membersWithInstructorMotoHSRole"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': membersWithInstructorMotoHSRole.indexOf(member) % 2 === 0,
                    'bg-row-dark': membersWithInstructorMotoHSRole.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td class="p-2">{{ member.motohs }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="membersWithInstructorMotoOffroadRole.length" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">Instructor Moto Offroad</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th class="p-2">Moto Offroad</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in membersWithInstructorMotoOffroadRole"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': membersWithInstructorMotoOffroadRole.indexOf(member) % 2 === 0,
                    'bg-row-dark': membersWithInstructorMotoOffroadRole.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td class="p-2">{{ member.motooffroad }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="membersWithInstructorMasinaHSRole.length" class="mb-6">
          <h2 class="text-xl font-bold mb-2 text-accent-light">Instructor Masina HS</h2>
          <div class="bg-primary-dark p-4 rounded">
            <table class="w-full text-left text-sm rounded overflow-hidden">
              <thead>
                <tr class="text-white bg-accent-dark">
                  <th class="p-2">Nume</th>
                  <th class="p-2">Masina HS</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in membersWithInstructorMasinaHSRole"
                  :key="member.discordID"
                  :class="{
                    'bg-row-light': membersWithInstructorMasinaHSRole.indexOf(member) % 2 === 0,
                    'bg-row-dark': membersWithInstructorMasinaHSRole.indexOf(member) % 2 !== 0,
                  }"
                >
                  <td class="p-2">{{ member.discord_name }}</td>
                  <td class="p-2">{{ member.hs }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <div v-else class="text-center text-gray-500">
        Nu există date disponibile.
      </div>
    </div>
  </div>

  <!-- Footer -->
  <FooterComponent />
</template>

<script>
import apiClient from "@/api/axiosInstance";
import SidebarComponent from "@/components/SidebarComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "RaportSaptamanalView",
  components: { SidebarComponent, FooterComponent },
  data() {
    return {
      raportData: [],
      filteredData: [],
      searchQuery: "",
      error: false,
      roleColumns: {
        "Chestor General": ["mandate_inchise", "razie_braco", "control_participat", "apel"],
        "Chestor": ["mandate_inchise", "razie_braco", "control_participat", "apel"],
        "Comisar": ["mandate_inchise", "regrupare", "razie_braco", "control_participat", "apel"],
        "Inspector": ["control_participat", "control_organizate", "regrupare", "apel"],
        "Agent sef principal": ["rutiera_participate", "control_participat", "rutiera_organizate", "control_organizate", "regrupare", "apel"],
        "Agent principal": ["rutiera_participate", "control_participat", "rutiera_organizate", "apel"],
        "Agent": ["rutiera_participate", "control_participat", "isctr", "apel"],
        "Cadet": ["rutiera_participate", "control_participat", "apel"],
      },
    };
  },
  computed: {
    groupedMembers() {
      const grouped = {};
      for (const role of Object.keys(this.roleColumns)) {
        const members = this.filteredData.filter(m => m.rol === role);
        if (members.length) grouped[role] = members;
      }
      return grouped;
    },
    membersWithResurseRole() {
      return this.filteredData.filter(m => m.roluri_aditionale.includes("Resurse"));
    },
    membersWithROLMDTRole() {
      return this.filteredData.filter(m => m.roluri_aditionale.includes("ROL_MDT"));
    },
    membersWithTesterMDTRole() {
      return this.filteredData.filter(m => m.roluri_aditionale.includes("Tester_MDT_Radio"));
    },
    membersWithInstructorPilotRole() {
      return this.filteredData.filter(m => m.roluri_aditionale.includes("Instructor_Pilot"));
    },
    membersWithInstructorMotoHSRole() {
      return this.filteredData.filter(m => m.roluri_aditionale.includes("Instructor_Moto_HS"));
    },
    membersWithInstructorMotoOffroadRole() {
      return this.filteredData.filter(m => m.roluri_aditionale.includes("Instructor_Moto_Offroad"));
    },
    membersWithInstructorMasinaHSRole() {
      return this.filteredData.filter(m => m.roluri_aditionale.includes("Instructor_Masina_HS"));
    },
  },
  created() {
    this.fetchRaport();
  },
  methods: {
    async fetchRaport() {
      try {
        const { data } = await apiClient.get("/api/raport-saptamanal");
        this.raportData = data;
        this.filteredData = data;
      } catch {
        this.error = true;
      }
    },
    handleSearch() {
      const q = this.searchQuery.toLowerCase();
      this.filteredData = this.raportData.filter(m =>
        m.discord_name.toLowerCase().includes(q)
      );
    },
    async generatePDF() {
      try {
        const response = await apiClient.post("/generate-pdf", {}, { responseType: "blob" });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "Raport_Saptamanal.pdf";
        link.click();
        URL.revokeObjectURL(link.href);
      } catch {
        alert("Eroare la generarea PDF-ului.");
      }
    },
  },
};
</script>

<style scoped>
.text-text-light { color: #b3b3cc; }
.text-accent-light { color: #a855f7; }
.bg-primary-dark { background-color: #1a1528; }
.bg-accent-dark { background-color: #a855f7; }
.bg-row-light { background-color: #262642; }
.bg-row-dark { background-color: #1e1e36; }
.placeholder-accent-light { color: #a855f7; }
.focus\:ring-accent-light:focus { box-shadow: 0 0 5px 2px #a855f7; }
</style>
